import React from 'react';
import { Tache } from '../types';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

interface KanbanBoardProps {
  taches: Tache[];
  canUpdateStatus: boolean;
  onUpdateStatus: (tacheId: string, etat: string) => Promise<void>;
  onCardClick?: (tache: Tache) => void;
}

const COLUMNS = [
  {
    id: 'en_attente',
    label: 'En attente',
    color: 'bg-gray-100',
    headerColor: 'bg-gray-200 text-gray-700',
    dotColor: 'bg-gray-400',
    count_bg: 'bg-gray-300 text-gray-700',
  },
  {
    id: 'en_cours',
    label: 'En cours',
    color: 'bg-blue-50',
    headerColor: 'bg-blue-100 text-blue-800',
    dotColor: 'bg-blue-500',
    count_bg: 'bg-blue-200 text-blue-800',
  },
  {
    id: 'terminee',
    label: 'Terminée',
    color: 'bg-green-50',
    headerColor: 'bg-green-100 text-green-800',
    dotColor: 'bg-green-500',
    count_bg: 'bg-green-200 text-green-800',
  },
] as const;

const complexiteBadge = (complexite: string) => {
  switch (complexite) {
    case 'facile':
      return <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full">Facile</span>;
    case 'difficile':
      return <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">Difficile</span>;
    default:
      return <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">Moyen</span>;
  }
};

const formatDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });

const daysLeft = (dateFin: string) => {
  const diff = Math.ceil((new Date(dateFin).getTime() - Date.now()) / 86400000);
  return diff;
};

const KanbanBoard: React.FC<KanbanBoardProps> = ({
  taches,
  canUpdateStatus,
  onUpdateStatus,
  onCardClick,
}) => {
  const byColumn = (etat: string) => taches.filter((t) => t.etat === etat);

  const moveCard = (tache: Tache, direction: 'forward' | 'back') => {
    const order = ['en_attente', 'en_cours', 'terminee'];
    const idx = order.indexOf(tache.etat);
    const next = direction === 'forward' ? order[idx + 1] : order[idx - 1];
    if (next) onUpdateStatus(tache.id, next);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-96">
      {COLUMNS.map((col) => {
        const cards = byColumn(col.id);
        return (
          <div key={col.id} className={`rounded-xl flex flex-col ${col.color}`}>
            {/* En-tête colonne */}
            <div className={`flex items-center justify-between px-4 py-3 rounded-t-xl ${col.headerColor}`}>
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${col.dotColor}`} />
                <span className="text-sm font-semibold">{col.label}</span>
              </div>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${col.count_bg}`}>
                {cards.length}
              </span>
            </div>

            {/* Cartes */}
            <div className="flex-1 p-3 space-y-3 overflow-y-auto max-h-[70vh]">
              {cards.length === 0 && (
                <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                  <DocumentTextIcon className="h-8 w-8 mb-1 opacity-40" />
                  <p className="text-xs">Aucune tâche</p>
                </div>
              )}

              {cards.map((tache) => {
                const dl = daysLeft(tache.dateFin);
                const isLate = tache.enRetard;
                const isDueSoon = !isLate && dl >= 0 && dl <= 5 && tache.etat !== 'terminee';

                return (
                  <div
                    key={tache.id}
                    className={`bg-white rounded-xl shadow-sm border overflow-hidden transition-shadow hover:shadow-md ${
                      isLate ? 'border-red-300' : isDueSoon ? 'border-amber-300' : 'border-gray-100'
                    } ${onCardClick ? 'cursor-pointer' : ''}`}
                    onClick={() => onCardClick && onCardClick(tache)}
                  >
                    {/* Barre colorée */}
                    <div className="h-1.5 w-full" style={{ backgroundColor: tache.couleur }} />

                    <div className="p-4 space-y-3">
                      {/* Titre + badges */}
                      <div>
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="text-sm font-semibold text-gray-900 leading-snug">{tache.nom}</h4>
                          {isLate && (
                            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                              <ExclamationTriangleIcon className="h-3 w-3" />
                              Retard
                            </span>
                          )}
                          {isDueSoon && (
                            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
                              <ClockIcon className="h-3 w-3" />
                              {dl}j
                            </span>
                          )}
                          {tache.etat === 'terminee' && !isLate && (
                            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                              <CheckCircleIcon className="h-3 w-3" />
                              OK
                            </span>
                          )}
                        </div>

                        {/* Description */}
                        {tache.description && (
                          <p className="mt-1.5 text-xs text-gray-500 line-clamp-2">{tache.description}</p>
                        )}
                      </div>

                      {/* Dates */}
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <CalendarDaysIcon className="h-3.5 w-3.5 flex-shrink-0" />
                        <span>{formatDate(tache.dateDebut)}</span>
                        <span>→</span>
                        <span className={isLate ? 'text-red-600 font-medium' : ''}>
                          {formatDate(tache.dateFin)}
                        </span>
                      </div>

                      {/* Assigné */}
                      {tache.assignedUserPrenom && (
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <UserIcon className="h-3 w-3 text-primary-600" />
                          </div>
                          <span className="text-xs text-gray-600 font-medium truncate">
                            {tache.assignedUserPrenom} {tache.assignedUserNom}
                          </span>
                        </div>
                      )}

                      {/* Complexité + livrables */}
                      <div className="flex flex-wrap items-center gap-2">
                        {complexiteBadge(tache.complexite)}
                        {tache.livrables && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <DocumentTextIcon className="h-3.5 w-3.5" />
                            Livrables
                          </span>
                        )}
                      </div>

                      {/* Actions de déplacement */}
                      {canUpdateStatus && (
                        <div
                          className="flex items-center justify-between pt-2 border-t border-gray-100"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {tache.etat !== 'en_attente' ? (
                            <button
                              onClick={() => moveCard(tache, 'back')}
                              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-2 py-1 rounded-lg transition-colors"
                            >
                              <ArrowLeftIcon className="h-3.5 w-3.5" />
                              {tache.etat === 'en_cours' ? 'En attente' : 'En cours'}
                            </button>
                          ) : <span />}

                          {tache.etat !== 'terminee' ? (
                            <button
                              onClick={() => moveCard(tache, 'forward')}
                              className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg transition-colors ${
                                tache.etat === 'en_cours'
                                  ? 'text-green-700 hover:bg-green-50'
                                  : 'text-blue-700 hover:bg-blue-50'
                              }`}
                            >
                              {tache.etat === 'en_attente' ? (
                                <>Démarrer <ArrowRightIcon className="h-3.5 w-3.5" /></>
                              ) : (
                                <>Terminer <CheckIcon className="h-3.5 w-3.5" /></>
                              )}
                            </button>
                          ) : (
                            <span className="text-xs text-green-600 font-medium">✓ Terminée</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KanbanBoard;
